// The in-game pause menu: ui-shell's GameOverlay, opened by the canvas gear
// or Escape. Chess-specific in its strings, the adapter below, and one extra
// action: the draw offer.
import { GameOverlay, type GameOverlayStrings } from '@bn-games/ui-shell';
import type { SettingsRow } from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';

export interface PauseHostDeps {
  readonly settingsRows: () => readonly SettingsRow[];
  readonly restart: () => void;
  readonly resign: () => void;
  readonly exitToMenu: () => void;
  readonly inGame: () => boolean;
  /** Online, Restart is a proposal the opponent must accept, one per game. */
  readonly isOnline: () => boolean;
  readonly restartState: () => { pending: boolean; canPropose: boolean };
  /** The draw offer as it stands; null when there is no game to offer it in. */
  readonly drawOffer: () => { readonly label: string; readonly enabled: boolean; readonly offer: () => void } | null;
}

const DRAW_ACTION_ID = 'draw';

function strings(): GameOverlayStrings {
  return {
    title: t('gameOverlay.title'),
    settingsLabel: t('gameOverlay.settingsLabel'),
    settingsBackLabel: t('gameOverlay.settingsBackLabel'),
    settingsViewTitle: t('gameOverlay.settingsViewTitle'),
    restartLabel: t('gameOverlay.restartLabel'),
    restartHintOnline: t('gameOverlay.restartHintOnline'),
    restartHintAlreadyProposed: t('gameOverlay.restartHintAlreadyProposed'),
    restartHintPending: t('gameOverlay.restartHintPending'),
    returnToMenuLabel: t('gameOverlay.returnToMenuLabel'),
    returnToMenuConfirmTitle: t('gameOverlay.returnToMenuConfirmTitle'),
    returnToMenuConfirmYes: t('gameOverlay.returnToMenuConfirmYes'),
    returnToMenuConfirmNo: t('gameOverlay.returnToMenuConfirmNo'),
    giveUpLabel: t('gameOverlay.giveUpLabel'),
    giveUpConfirmTitle: t('gameOverlay.giveUpConfirmTitle'),
    giveUpConfirmYes: t('gameOverlay.giveUpConfirmYes'),
    giveUpConfirmNo: t('gameOverlay.giveUpConfirmNo'),
    close: t('gameOverlay.close'),
    settingsTabs: {
      gameplay: t('gameOverlay.settingsTabs.gameplay'),
      interface: t('gameOverlay.settingsTabs.interface'),
      audio: t('gameOverlay.settingsTabs.audio'),
    },
  };
}

export class PauseHost {
  private overlay: GameOverlay;

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape' || (!this.overlay.isOpen() && !this.deps.inGame())) return;
    // Another dialog (promotion, draw offer, game over) owns Escape while it is up.
    if (!this.overlay.isOpen() && document.querySelector('.modal-overlay') !== null) return;
    e.preventDefault();
    this.overlay.toggle();
  };

  constructor(private readonly deps: PauseHostDeps) {
    this.overlay = this.build();
    window.addEventListener('keydown', this.onKeyDown);
  }

  private build(): GameOverlay {
    return new GameOverlay({
      strings: strings(),
      context: {
        getSettingsRows: () => this.deps.settingsRows(),
        requestRestart: () => {
          // Online the menu stays open to show the proposal as pending.
          if (!this.deps.isOnline()) this.overlay.close();
          this.deps.restart();
        },
        acceptRestart: () => {},
        declineRestart: () => {},
        requestResign: () => {
          this.overlay.close();
          this.deps.resign();
        },
        exitToMainMenu: () => {
          this.overlay.close();
          this.deps.exitToMenu();
        },
        isOnline: () => this.deps.isOnline(),
        getRestartState: () => this.deps.restartState(),
        getExtraActions: () => {
          const draw = this.deps.drawOffer();
          if (draw === null) return [];
          return [{
            id: DRAW_ACTION_ID,
            label: draw.label,
            enabled: draw.enabled,
            // The answer comes as a dialog (bot, hot-seat) or a banner (online): get out of its way.
            onSelect: () => {
              this.overlay.close();
              draw.offer();
            },
          }];
        },
      },
    });
  }

  open(): void {
    this.overlay.open();
  }

  isOpen(): boolean {
    return this.overlay.isOpen();
  }

  close(): void {
    this.overlay.close();
  }

  /** The online game moved on: update the Restart and draw rows if they are showing. */
  refreshRestart(): void {
    if (!this.overlay.isOpen()) return;
    this.overlay.refreshRestartState();
    this.overlay.refreshExtraActions();
  }

  /** Language changed: rebuild so the next open uses the new strings. */
  refresh(): void {
    const wasOpen = this.overlay.isOpen();
    this.overlay.close();
    this.overlay = this.build();
    if (wasOpen) this.overlay.open();
  }
}
